import type {VadoCatalog} from "#pricing/catalogs/vado/catalog"
import type {KeyedVariants, PriceGraphData} from "#pricing/catalogs/graph-helpers"
import {and, createConditionedPriceNodes, createKeyedVariantPriceNodes, createPriceContainer, not, variantSelected} from "#pricing/catalogs/graph-helpers"
import {ConditionalAmountGroupNode} from "#pricing/nodes/conditional-amount-group-node"
import {ConditionalAmountList} from "#pricing/nodes/conditional-amount-list"
import {ConditionalAmountNode} from "#pricing/nodes/conditional-amount-node"
import {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import type {PricedItemNode} from "#pricing/nodes/priced-item-node"

/**
 * Same graph as createCameoPriceGraph (cameo-graph.ts), built with VariantSelectedCondition instead of the deprecated
 * VariantConditionNode. Both exist in parallel until the stored graphs are migrated; they must evaluate identically.
 */
export function createCameoPriceGraphV2(catalog: VadoCatalog): PriceGraphData {
    //---------------------------------------------Configuration groups---------------------------------------------
    const furnitureWidth = new ConfigurationGroupNode({
        parameters: {
            groupId: "furniture-width",
            groupName: "",
            currentVariant: {groupId: "furniture-width", groupName: "", variantId: "400", variantName: ""},
            allVariants: [
                {groupId: "furniture-width", groupName: "", variantId: "400", variantName: ""},
                {groupId: "furniture-width", groupName: "", variantId: "500", variantName: ""},
                {groupId: "furniture-width", groupName: "", variantId: "600", variantName: ""},
                {groupId: "furniture-width", groupName: "", variantId: "800", variantName: ""},
                {groupId: "furniture-width", groupName: "", variantId: "1200", variantName: ""},
            ],
        },
    })

    const LeverOption = new ConfigurationGroupNode({
        parameters: {
            groupId: "lever-option",
            groupName: "",
            currentVariant: {groupId: "lever-option", groupName: "", variantId: "lever", variantName: ""},
            allVariants: [
                {groupId: "lever-option", groupName: "", variantId: "lever", variantName: ""},
                {groupId: "lever-option", groupName: "", variantId: "leverless", variantName: ""},
            ],
        },
    })

    const TapFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "3acf90cf-dfd6-4043-be66-cb0590cfd635",
            groupName: "",
            currentVariant: {
                groupId: "3acf90cf-dfd6-4043-be66-cb0590cfd635",
                groupName: "",
                variantId: "4022413b-478d-4a96-88e7-9c99bcd96546",
                variantName: "",
            },
            allVariants: [
                {groupId: "3acf90cf-dfd6-4043-be66-cb0590cfd635", groupName: "", variantId: "4022413b-478d-4a96-88e7-9c99bcd96546", variantName: ""},
                {groupId: "3acf90cf-dfd6-4043-be66-cb0590cfd635", groupName: "", variantId: "6de4129f-561c-4365-a832-15cd95eefd3a", variantName: ""},
                {groupId: "3acf90cf-dfd6-4043-be66-cb0590cfd635", groupName: "", variantId: "6d20d045-897c-4aef-8368-d622157a2ec3", variantName: ""},
                {groupId: "3acf90cf-dfd6-4043-be66-cb0590cfd635", groupName: "", variantId: "5dd635b6-9803-4ce7-ba91-e6c2cecf4e8f", variantName: ""},
            ],
        },
    })

    const HandleFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "408cbb56-d6a4-444a-87f6-dfb0fe49f48e",
            groupName: "",
            currentVariant: {
                groupId: "408cbb56-d6a4-444a-87f6-dfb0fe49f48e",
                groupName: "",
                variantId: "fb08d24f-d153-4752-a60e-c577c67e2605",
                variantName: "",
            },
            allVariants: [
                {groupId: "408cbb56-d6a4-444a-87f6-dfb0fe49f48e", groupName: "", variantId: "fb08d24f-d153-4752-a60e-c577c67e2605", variantName: ""},
                {groupId: "408cbb56-d6a4-444a-87f6-dfb0fe49f48e", groupName: "", variantId: "5eb302cf-8b60-4ca4-b1a4-1677da74e3af", variantName: ""},
                {groupId: "408cbb56-d6a4-444a-87f6-dfb0fe49f48e", groupName: "", variantId: "2b4174b9-fe15-4c05-ae69-60faf93e5deb", variantName: ""},
                {groupId: "408cbb56-d6a4-444a-87f6-dfb0fe49f48e", groupName: "", variantId: "d80de114-01d0-4190-8ce5-9cbbec4846d3", variantName: ""},
            ],
        },
    })

    const Handle = new ConfigurationGroupNode({
        parameters: {
            groupId: "handle",
            groupName: "",
            currentVariant: {groupId: "handle", groupName: "", variantId: "top-mount-left", variantName: ""},
            allVariants: [
                {groupId: "handle", groupName: "", variantId: "top-mount-left", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "top-mount-right", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "bar", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "knob", variantName: ""},
                {groupId: "handle", groupName: "", variantId: "top-mount", variantName: ""},
            ],
        },
    })

    const FittingOption = new ConfigurationGroupNode({
        parameters: {
            groupId: "fitting-option",
            groupName: "",
            currentVariant: {groupId: "fitting-option", groupName: "", variantId: "wall-mounted", variantName: ""},
            allVariants: [
                {groupId: "fitting-option", groupName: "", variantId: "wall-mounted", variantName: ""},
                {groupId: "fitting-option", groupName: "", variantId: "floor-mounted", variantName: ""},
            ],
        },
    })

    const BasinOrientation = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin-orientation",
            groupName: "",
            currentVariant: {groupId: "basin-orientation", groupName: "", variantId: "left", variantName: ""},
            allVariants: [
                {groupId: "basin-orientation", groupName: "", variantId: "left", variantName: ""},
                {groupId: "basin-orientation", groupName: "", variantId: "right", variantName: ""},
            ],
        },
    })

    const BasinDesign = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin-design",
            groupName: "",
            currentVariant: {groupId: "basin-design", groupName: "", variantId: "ceramic", variantName: ""},
            allVariants: [
                {groupId: "basin-design", groupName: "", variantId: "ceramic", variantName: ""},
                {groupId: "basin-design", groupName: "", variantId: "mineral", variantName: ""},
                {groupId: "basin-design", groupName: "", variantId: "mineral-countertop", variantName: ""},
            ],
        },
    })

    const BasinFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "c3030e94-f7bf-4ae0-a714-bfa2fc055c94",
            groupName: "",
            currentVariant: {
                groupId: "c3030e94-f7bf-4ae0-a714-bfa2fc055c94",
                groupName: "",
                variantId: "36458f8a-5bd0-43a5-969c-25cc8ca03c8b",
                variantName: "",
            },
            allVariants: [
                {groupId: "c3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "36458f8a-5bd0-43a5-969c-25cc8ca03c8b", variantName: ""},
                {groupId: "c3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "50eaa083-5a9a-4058-a33e-acb6a3051f28", variantName: ""},
                {groupId: "c3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "e3adfd05-dfd9-417d-b779-d7d6602d29bc", variantName: ""},
                {groupId: "c3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "587a81be-ccaa-4c99-9ada-7790e7eba2b4", variantName: ""},
                //quirk: added by the finish map, not a configurator variant
                {groupId: "c3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "stone-grey", variantName: ""},
            ],
        },
    })

    const FurnitureFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94",
            groupName: "",
            currentVariant: {
                groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94",
                groupName: "",
                variantId: "50eaa083-5a9a-4058-a33e-acb6a3051f28",
                variantName: "",
            },
            allVariants: [
                {groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "50eaa083-5a9a-4058-a33e-acb6a3051f28", variantName: ""},
                {groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "e3adfd05-dfd9-417d-b779-d7d6602d29bc", variantName: ""},
                {groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "587a81be-ccaa-4c99-9ada-7790e7eba2b4", variantName: ""},
                {groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "c0e9884a-8f72-485e-82a2-113ad8190c36", variantName: ""},
                {groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "34a6d81a-0852-4ba2-be44-e7eca1c79430", variantName: ""},
                //quirk: added by the finish map, not configurator variants
                {groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "eucalyptus-green", variantName: ""},
                {groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "stone-grey", variantName: ""},
                {groupId: "a3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "warm-wallnut", variantName: ""},
            ],
        },
    })

    const Worktopfinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94",
            groupName: "",
            currentVariant: {
                groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94",
                groupName: "",
                variantId: "36458f8a-5bd0-43a5-969c-25cc8ca03c8b",
                variantName: "",
            },
            allVariants: [
                {groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "36458f8a-5bd0-43a5-969c-25cc8ca03c8b", variantName: ""},
                {groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "50eaa083-5a9a-4058-a33e-acb6a3051f28", variantName: ""},
                {groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "e3adfd05-dfd9-417d-b779-d7d6602d29bc", variantName: ""},
                {groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "587a81be-ccaa-4c99-9ada-7790e7eba2b4", variantName: ""},
                {groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "stone-grey", variantName: ""},
                {groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "warm-wallnut", variantName: ""},
                {groupId: "b3030e94-f7bf-4ae0-a714-bfa2fc055c94", groupName: "", variantId: "natural-oak", variantName: ""},
            ],
        },
    })

    const BasinWaste = new ConfigurationGroupNode({
        parameters: {
            groupId: "10e620bf-de82-4cfa-bb49-ed8b7f90adab",
            groupName: "",
            currentVariant: {
                groupId: "10e620bf-de82-4cfa-bb49-ed8b7f90adab",
                groupName: "",
                variantId: "fb291d04-d423-447b-95e5-35a7c81e5d10",
                variantName: "",
            },
            allVariants: [
                {groupId: "10e620bf-de82-4cfa-bb49-ed8b7f90adab", groupName: "", variantId: "fb291d04-d423-447b-95e5-35a7c81e5d10", variantName: ""},
                {groupId: "10e620bf-de82-4cfa-bb49-ed8b7f90adab", groupName: "", variantId: "721829e7-bc50-4279-981c-8fd6181a3d4a", variantName: ""},
            ],
        },
    })

    const OverflowFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "overflow_finish",
            groupName: "",
            currentVariant: {groupId: "overflow_finish", groupName: "", variantId: "chrome", variantName: ""},
            allVariants: [
                {groupId: "overflow_finish", groupName: "", variantId: "chrome", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "white", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "satin", variantName: ""},
                {groupId: "overflow_finish", groupName: "", variantId: "black", variantName: ""},
            ],
        },
    })

    const BasinOverflow = new ConfigurationGroupNode({
        parameters: {
            groupId: "overflow",
            groupName: "",
            currentVariant: {groupId: "overflow", groupName: "", variantId: "w_overflow", variantName: ""},
            allVariants: [
                {groupId: "overflow", groupName: "", variantId: "w_overflow", variantName: ""},
                {groupId: "overflow", groupName: "", variantId: "wo_overflow", variantName: ""},
            ],
        },
    })

    const VanityLegs = new ConfigurationGroupNode({
        parameters: {
            groupId: "legs",
            groupName: "",
            currentVariant: {groupId: "legs", groupName: "", variantId: "yes", variantName: ""},
            allVariants: [
                {groupId: "legs", groupName: "", variantId: "yes", variantName: ""},
                {groupId: "legs", groupName: "", variantId: "no", variantName: ""},
            ],
        },
    })

    const LegFinish = new ConfigurationGroupNode({
        parameters: {
            groupId: "leg_finish",
            groupName: "",
            currentVariant: {groupId: "leg_finish", groupName: "", variantId: "arctic_white", variantName: ""},
            allVariants: [
                {groupId: "leg_finish", groupName: "", variantId: "arctic_white", variantName: ""},
                {groupId: "leg_finish", groupName: "", variantId: "cove_blue", variantName: ""},
                {groupId: "leg_finish", groupName: "", variantId: "pink_clay", variantName: ""},
                {groupId: "leg_finish", groupName: "", variantId: "atlantic_blue", variantName: ""},
                {groupId: "leg_finish", groupName: "", variantId: "matt_black", variantName: ""},
                //quirk: added by the finish map, not configurator variants
                {groupId: "leg_finish", groupName: "", variantId: "eucalyptus-green", variantName: ""},
                {groupId: "leg_finish", groupName: "", variantId: "stone-grey", variantName: ""},
            ],
        },
    })

    const BasinShape = new ConfigurationGroupNode({
        parameters: {
            groupId: "basin-shape",
            groupName: "",
            currentVariant: {groupId: "basin-shape", groupName: "", variantId: "round", variantName: ""},
            allVariants: [
                {groupId: "basin-shape", groupName: "", variantId: "round", variantName: ""},
                {groupId: "basin-shape", groupName: "", variantId: "oval", variantName: ""},
            ],
        },
    })

    const TapType = new ConfigurationGroupNode({
        parameters: {
            groupId: "tap-type",
            groupName: "",
            currentVariant: {groupId: "tap-type", groupName: "", variantId: "deck-mounted", variantName: ""},
            allVariants: [
                {groupId: "tap-type", groupName: "", variantId: "deck-mounted", variantName: ""},
                {groupId: "tap-type", groupName: "", variantId: "wall-mounted", variantName: ""},
            ],
        },
    })

    //---------------------------------------------Keyed variants (catalog key → config variant)---------------------------------------------

    const keyed = (group: ConfigurationGroupNode, entries: [string, string][]): KeyedVariants => ({groups: [group], variantIdByKey: new Map(entries)})

    const furnitureFinishVariants = keyed(FurnitureFinish, [
        ["Arctic White", "50eaa083-5a9a-4058-a33e-acb6a3051f28"],
        ["Cove Blue", "e3adfd05-dfd9-417d-b779-d7d6602d29bc"],
        ["Pink Clay", "587a81be-ccaa-4c99-9ada-7790e7eba2b4"],
        ["Atlantic Blue", "c0e9884a-8f72-485e-82a2-113ad8190c36"],
        ["Natural Oak", "34a6d81a-0852-4ba2-be44-e7eca1c79430"],
        ["Stone Grey", "stone-grey"],
        ["Eucalyptus Green", "eucalyptus-green"],
        ["Walnut", "warm-wallnut"],
    ])

    const tapFinishVariants = keyed(TapFinish, [
        ["Chrome", "4022413b-478d-4a96-88e7-9c99bcd96546"],
        ["Matt Black", "6de4129f-561c-4365-a832-15cd95eefd3a"],
        ["Matt White", "6d20d045-897c-4aef-8368-d622157a2ec3"],
        ["Satin Brass", "5dd635b6-9803-4ce7-ba91-e6c2cecf4e8f"],
    ])

    const handleFinishVariants = keyed(HandleFinish, [
        ["Chrome", "fb08d24f-d153-4752-a60e-c577c67e2605"],
        ["Matt Black", "5eb302cf-8b60-4ca4-b1a4-1677da74e3af"],
        ["Matt White", "2b4174b9-fe15-4c05-ae69-60faf93e5deb"],
        ["Satin Brass", "d80de114-01d0-4190-8ce5-9cbbec4846d3"],
    ])

    const overflowFinishVariants = keyed(OverflowFinish, [
        ["Chrome", "chrome"],
        ["Matt Black", "black"],
        ["Matt White", "white"],
        ["Satin Brass", "satin"],
    ])

    const basinFinishVariants = keyed(BasinFinish, [
        ["Gloss White", "36458f8a-5bd0-43a5-969c-25cc8ca03c8b"],
        ["Arctic White", "50eaa083-5a9a-4058-a33e-acb6a3051f28"],
        ["Cove Blue", "e3adfd05-dfd9-417d-b779-d7d6602d29bc"],
        ["Pink Clay", "587a81be-ccaa-4c99-9ada-7790e7eba2b4"],
        ["Stone Grey", "stone-grey"],
    ])

    const legFinishVariants = keyed(LegFinish, [
        ["Arctic White", "arctic_white"],
        ["Cove Blue", "cove_blue"],
        ["Pink Clay", "pink_clay"],
        ["Atlantic Blue", "atlantic_blue"],
        ["Matt Black", "matt_black"],
        ["Eucalyptus Green", "eucalyptus-green"],
        ["Stone Grey", "stone-grey"],
    ])

    const worktopFinishVariants = keyed(Worktopfinish, [
        ["Gloss White", "36458f8a-5bd0-43a5-969c-25cc8ca03c8b"],
        ["Arctic White", "50eaa083-5a9a-4058-a33e-acb6a3051f28"],
        ["Cove Blue", "e3adfd05-dfd9-417d-b779-d7d6602d29bc"],
        ["Pink Clay", "587a81be-ccaa-4c99-9ada-7790e7eba2b4"],
        ["Stone Grey", "stone-grey"],
        ["Walnut", "warm-wallnut"],
        ["Natural Oak", "natural-oak"],
    ])

    const result: PriceGraphData = {
        amountGroups: [],
        pricedItemNodes: [],
        priceContainers: [],
    }

    //---------------------------------------------Handle amount---------------------------------------------

    const handleAmountGroup = new ConditionalAmountGroupNode({
        parameters: {
            defaultAmount: 1,
            description: "Handle amount",
            amountNodes: new ConditionalAmountList({
                parameters: {
                    list: [
                        new ConditionalAmountNode({
                            parameters: {description: "Handle: 2", amount: 2, condition: variantSelected(furnitureWidth, ["500", "600", "800"])},
                        }),
                        new ConditionalAmountNode({parameters: {description: "Handle: 4", amount: 4, condition: variantSelected(furnitureWidth, "1200")}}),
                    ],
                },
            }),
        },
    })
    result.amountGroups.push(handleAmountGroup)

    //---------------------------------------------Bar/knob handle (shared across all units with different amounts)---------------------------------------------

    //containers are added further below to keep the output order of the api consistent
    const handleBar = createPriceContainer(
        "Handle bar",
        "Handle bar",
        createKeyedVariantPriceNodes(catalog, "Furniture Bar Handle", 4, handleFinishVariants, {amount: handleAmountGroup}),
        variantSelected(Handle, "bar"),
    )

    const handleKnob = createPriceContainer(
        "Handle knob",
        "Handle knob",
        createKeyedVariantPriceNodes(catalog, "Furniture Knob", 4, handleFinishVariants, {amount: handleAmountGroup}),
        variantSelected(Handle, "knob"),
    )

    //---------------------------------------------Lever amount---------------------------------------------

    const leverAmountGroup = new ConditionalAmountGroupNode({
        parameters: {
            defaultAmount: 1,
            description: "Lever amount",
            amountNodes: new ConditionalAmountList({
                parameters: {
                    list: [new ConditionalAmountNode({parameters: {description: "Lever: 2", amount: 2, condition: variantSelected(furnitureWidth, "1200")}})],
                },
            }),
        },
    })
    result.amountGroups.push(leverAmountGroup)

    //---------------------------------------------Taps for 500/600/800/1200 (shared across those, 400 has its own)---------------------------------------------

    const tapNodes = (terms: string, amount?: ConditionalAmountGroupNode, termExclude?: string[]) =>
        createKeyedVariantPriceNodes(catalog, terms, 4, tapFinishVariants, {amount, termExclude})

    const leverless = (description: string, items: PricedItemNode[]) =>
        createPriceContainer(description, description, items, variantSelected(LeverOption, "leverless"))
    const lever = (description: string, items: PricedItemNode[]) => createPriceContainer(description, description, items, variantSelected(LeverOption, "lever"))

    //extended: no basin waste -> used with countertop basin
    const containerCountertopDeckTap = createPriceContainer(
        "Deck tap countertop 600/800/1200",
        "Deck tap countertop 600/800/1200",
        [
            leverless("Extended Leverless tap 600/800/1200", tapNodes("Cameo Leverless Extended Mono Basin Mixer 5 l/min Flow Regulator", leverAmountGroup)),
            lever("Extended Lever tap 600/800/1200", tapNodes("Cameo Lever Extended Mono Basin Mixer 5 l/min Flow Regulator", leverAmountGroup)),
        ],
        variantSelected(TapType, "deck-mounted"),
    )

    //wall: no basin waste -> used with countertop basin
    const containerCountertopWallTap = createPriceContainer(
        "Wall tap countertop 600/800/1200",
        "Wall tap countertop 600/800/1200",
        [
            leverless("Wall Leverless tap 600/800/1200", tapNodes("Cameo Leverless Wall Mounted Basin Mixer 5 l/min Flow Regulator", leverAmountGroup)),
            lever("Wall Lever tap 600/800/1200", tapNodes("Cameo Lever Wall Mounted Basin Mixer 5 l/min Flow Regulator", leverAmountGroup)),
        ],
        variantSelected(TapType, "wall-mounted"),
    )

    //the countertop taps ship without basin waste, so it is priced separately; searched by SKU, as the description also matches the bundled mixers
    const containerCountertopBasinWaste = createPriceContainer(
        "Basin waste countertop 600/800/1200",
        "Basin waste countertop 600/800/1200",
        tapNodes("WG-395", leverAmountGroup, ["Pewt"]), //Pewter is only used for Safari
        variantSelected(BasinWaste, "fb291d04-d423-447b-95e5-35a7c81e5d10"),
    )

    const containerTapsCountertop = createPriceContainer(
        "Taps countertop basin 600/800/1200",
        "Taps countertop basin 600/800/1200",
        [containerCountertopDeckTap, containerCountertopWallTap, containerCountertopBasinWaste],
        variantSelected(BasinDesign, "mineral-countertop"),
    )

    const containerTapsWithoutBasinWaste = createPriceContainer(
        "Taps without basin waste 500/-1200",
        "Taps without basin waste 500/-1200",
        [
            leverless("Mono Leverless tap 500/600/800/1200", tapNodes("Cameo Leverless Mono Basin Mixer 5 l/min Flow Regulator, ", leverAmountGroup)),
            lever("Mono Lever tap 500/600/800/1200", tapNodes("Cameo Lever Mono Basin Mixer 5 l/min Flow Regulator, ", leverAmountGroup)),
        ],
        variantSelected(BasinWaste, "721829e7-bc50-4279-981c-8fd6181a3d4a"),
    )

    const containerTapsWithBasinWaste = createPriceContainer(
        "Taps with basin waste 500/-1200",
        "Taps with basin waste 500/-1200",
        [
            lever(
                "Mono Lever tap 500/600/800/1200 basin waste",
                tapNodes("Cameo Lever Mono Basin Mixer 5 l/min Flow Regulator + Universal Basin Waste, ", leverAmountGroup),
            ),
            leverless(
                "Mono Leverless tap 500/600/800/1200 basin waste",
                tapNodes("Cameo Leverless Mono Basin Mixer 5 l/min Flow Regulator + Universal Basin Waste, ", leverAmountGroup),
            ),
        ],
        variantSelected(BasinWaste, "fb291d04-d423-447b-95e5-35a7c81e5d10"),
    )

    const containerTapsNonCountertop = createPriceContainer(
        "Taps non-countertop basin 500/600/800/1200",
        "Taps non-countertop basin 500/600/800/1200",
        [containerTapsWithoutBasinWaste, containerTapsWithBasinWaste],
        not(variantSelected(BasinDesign, "mineral-countertop")),
    )

    //---------------------------------------------Countertop basin---------------------------------------------

    const countertopBasinShape = (description: string, variantId: string) =>
        createPriceContainer(
            description,
            description,
            createKeyedVariantPriceNodes(catalog, description, 5, basinFinishVariants, {amount: leverAmountGroup}),
            variantSelected(BasinShape, variantId),
        )

    const countertopBasin = createPriceContainer(
        "Countertop basin",
        "Countertop basin",
        [countertopBasinShape("Countertop basin round", "round"), countertopBasinShape("Countertop basin oval", "oval")],
        variantSelected(BasinDesign, "mineral-countertop"),
    )

    //---------------------------------------------Shared building blocks---------------------------------------------

    const ceramicBasin = (terms: string) => createConditionedPriceNodes(catalog, terms, 1, variantSelected(BasinDesign, "ceramic"))

    const mineralBasin = (terms: string) =>
        createPriceContainer(
            "Basin design mineral",
            "mineral",
            createKeyedVariantPriceNodes(catalog, terms, 3, basinFinishVariants),
            variantSelected(BasinDesign, "mineral"),
        )

    const worktop = (size: string) =>
        createPriceContainer(
            `Worktop ${size}`,
            `Worktop ${size}`,
            createKeyedVariantPriceNodes(catalog, `Worktop ${size}`, 7, worktopFinishVariants),
            variantSelected(BasinDesign, "mineral-countertop"),
        )

    /** Top mount handle pair, priced as two single handles. */
    const topMountHandle = (description: string, terms: string) => {
        const items = createKeyedVariantPriceNodes(catalog, terms, 4, handleFinishVariants, {amount: 2})
        items.forEach((node) => node.updateParameters({description: node.parameters.description.replace("Handle Pair", "Handle")}))
        return createPriceContainer(description, description, items, variantSelected(Handle, "top-mount"))
    }

    //---------------------------------------------400mm price container---------------------------------------------

    const basinOrientation = (description: string, ceramicTerms: string, mineralTerms: string, variantId: string) =>
        createPriceContainer(
            description,
            description,
            [...ceramicBasin(ceramicTerms), ...createKeyedVariantPriceNodes(catalog, mineralTerms, 3, basinFinishVariants)],
            variantSelected(BasinOrientation, variantId),
        )

    const handleOrientation400 = (description: string, terms: string, variantId: string) =>
        createPriceContainer(
            description,
            description,
            createKeyedVariantPriceNodes(catalog, terms, 4, handleFinishVariants),
            variantSelected(Handle, variantId),
        )

    const container400mm = createPriceContainer(
        "400mm",
        "400mm",
        [
            createPriceContainer(
                "Fitting option wall mounted",
                "Fitting option wall mounted",
                createKeyedVariantPriceNodes(catalog, "400mm wall hung Cloakroom Unit with reversible door", 8, furnitureFinishVariants),
                variantSelected(FittingOption, "wall-mounted"),
            ),
            createPriceContainer(
                "Fitting option floor mounted",
                "Fitting option floor mounted",
                createKeyedVariantPriceNodes(catalog, "400mm floor mounted Cloakroom Unit with reversible door", 8, furnitureFinishVariants),
                variantSelected(FittingOption, "floor-mounted"),
            ),
            basinOrientation("Basin orientation left", "ceramic furniture basin, left tap hole", "mineral cast, left tap hole", "left"),
            basinOrientation("Basin orientation right", "ceramic furniture basin, right tap hole", "mineral cast, right tap hole", "right"),
            leverless("Lever option leverless basin waste", tapNodes("Cameo Leverless Mini Mono Basin Mixer + Universal Basin Waste")),
            lever("Lever option lever basin waste", tapNodes("Cameo Lever Mini Mono Basin Mixer + Universal Basin Waste")),
            handleBar,
            handleKnob,
            handleOrientation400("Handle orientation left", "400mm Furniture Top Mount Handle, Left Pul", "top-mount-left"),
            handleOrientation400("Handle orientation right", "400mm Furniture Top Mount Handle, Right Pul", "top-mount-right"),
        ],
        variantSelected(furnitureWidth, "400"),
    )
    result.priceContainers.push(container400mm)

    //---------------------------------------------500mm price container---------------------------------------------

    const container500mm = createPriceContainer(
        "500mm",
        "500mm",
        [
            ...createKeyedVariantPriceNodes(catalog, "500mm vanity unit with 2 drawers", 8, furnitureFinishVariants),
            ...ceramicBasin("500mm Basin, Ceramic"),
            mineralBasin("500mm basin, mineral cast"),
            countertopBasin,
            containerTapsCountertop,
            containerTapsNonCountertop,
            //the 500mm handle (CAM-HAN5-*) carries no size in its description, unlike the 600/800/1200 ones
            topMountHandle("Handle 500mm", "Cameo Top Mount Handle"),
            handleBar,
            handleKnob,
        ],
        variantSelected(furnitureWidth, "500"),
    )
    result.priceContainers.push(container500mm)

    //---------------------------------------------600/800mm price containers---------------------------------------------

    const container600_800 = (size: string) =>
        createPriceContainer(
            `${size}mm`,
            `${size}mm`,
            [
                ...createKeyedVariantPriceNodes(catalog, `${size}mm vanity unit with 2 drawers`, 8, furnitureFinishVariants),
                ...ceramicBasin(`${size}mm ceramic furniture basin`),
                mineralBasin(`Cameo ${size}mm basin, mineral cast`),
                countertopBasin,
                worktop(`${size}mm`),
                containerTapsCountertop,
                containerTapsNonCountertop,
                topMountHandle(`Handle ${size}mm`, `${size}mm Furniture Top Mount Handle`),
                handleBar,
                handleKnob,
            ],
            variantSelected(furnitureWidth, size),
        )
    result.priceContainers.push(container600_800("600"), container600_800("800"))

    //---------------------------------------------1200mm price container---------------------------------------------

    const handle1200 = (terms: string) => createKeyedVariantPriceNodes(catalog, terms, 4, handleFinishVariants, {amount: 2})

    const container1200mm = createPriceContainer(
        "1200mm",
        "1200mm",
        [
            ...createKeyedVariantPriceNodes(catalog, "1200mm vanity unit with 4 drawers", 8, furnitureFinishVariants),
            ...ceramicBasin("1200mm ceramic furniture basin"),
            mineralBasin("1200mm basin, mineral cast"),
            countertopBasin,
            worktop("1200mm"),
            containerTapsCountertop,
            containerTapsNonCountertop,
            createPriceContainer(
                "Handle 1200mm",
                "Handle 1200mm",
                [...handle1200("1200mm Furniture Top Mount Handle, Left"), ...handle1200("1200mm Furniture Top Mount Handle, Right")],
                variantSelected(Handle, "top-mount"),
            ),
            handleBar,
            handleKnob,
        ],
        variantSelected(furnitureWidth, "1200"),
    )
    result.priceContainers.push(container1200mm)

    //---------------------------------------------Overflow covers---------------------------------------------

    //quirk: the mineral overflow cover is keyed on the tap finish group, the ceramic one on the overflow finish group
    const containerOverflowCoverMineral = createPriceContainer(
        "Overflow cover mineral",
        "Overflow cover mineral",
        tapNodes("Mineral Basin Overflow Cover", leverAmountGroup, ["Pewt"]), //Pewter is only used for Safari
        variantSelected(BasinDesign, "mineral"),
    )
    result.priceContainers.push(containerOverflowCoverMineral)

    const containerOverflowCoverCeramic = createPriceContainer(
        "Overflow cover ceramic",
        "Overflow cover ceramic",
        createKeyedVariantPriceNodes(catalog, "Ceramic Basin Overflow Cover", 4, overflowFinishVariants, {amount: leverAmountGroup, termExclude: ["Pewt"]}), //Pewter is only used for Safari
        and(variantSelected(BasinDesign, "ceramic"), variantSelected(BasinOverflow, "w_overflow")),
    )
    result.priceContainers.push(containerOverflowCoverCeramic)

    //---------------------------------------------Legs---------------------------------------------

    const legsContainer = createPriceContainer(
        "Vanity legs",
        "Vanity legs",
        [
            ...createKeyedVariantPriceNodes(catalog, "Furniture Front Legs Pair", 7, legFinishVariants),
            ...createKeyedVariantPriceNodes(catalog, "Furniture Rear Legs Pair", 7, legFinishVariants),
        ],
        variantSelected(VanityLegs, "yes"),
    )
    result.priceContainers.push(legsContainer)

    return result
}
